/**
 * Permission Utility Functions
 *
 * Provides functions to check user permissions
 */

import Cookies from 'js-cookie';
import store from '../store';

/**
 * Get permissions from Redux store first, then fall back to cookies
 * @returns {Array|null}
 */
export const getPermissions = () => {
  // Try Redux store first (most reliable)
  try {
    const state = store.getState();
    const reduxPermissions = state.login?.permissions;

    if (reduxPermissions) {
      return reduxPermissions;
    }
  } catch (error) {
    // Silently fall back to cookies if Redux fails
  }

  // Fall back to cookies if Redux doesn't have permissions
  const permissions = Cookies.get('permissions');
  return permissions ? JSON.parse(permissions) : null;
};

/**
 * Transform permissions array to flat object for easier lookup
 * Input: [{ role: [{ key: "addRole", value: true }, ...] }, { user: [...] }]
 * Output: { role: { addRole: true, deleteRole: false }, user: { addUser: true } }
 * @param {Array} permissions
 * @returns {Object}
 */
export const transformPermissions = (permissions) => {
  if (!permissions || !Array.isArray(permissions)) return {};

  const result = {};
  permissions.forEach((categoryObj) => {
    const categoryName = Object.keys(categoryObj)[0];
    const perms = categoryObj[categoryName];

    if (Array.isArray(perms)) {
      result[categoryName] = {};
      perms.forEach((perm) => {
        result[categoryName][perm.key] = perm.value || false;
      });
    }
  });

  return result;
};

/**
 * Check if user has a specific permission
 * @param {string} module - Module name (e.g., 'role', 'user')
 * @param {string} action - Action name (e.g., 'addRole', 'viewUser')
 * @returns {boolean}
 */
export const hasPermission = (module, action) => {
  const permissions = getPermissions();
  if (!permissions) {
    return false;
  }

  const transformed = transformPermissions(permissions);
  return transformed[module]?.[action] === true;
};

/**
 * Check if user has ANY of the specified permissions
 * @param {Array} permissionList - Array of [module, action] pairs
 * Example: [['role', 'viewRole'], ['user', 'viewUser']]
 * @returns {boolean}
 */
export const hasAnyPermission = (permissionList) => {
  return permissionList.some(([module, action]) => hasPermission(module, action));
};

/**
 * Check if user has ALL of the specified permissions
 * @param {Array} permissionList - Array of [module, action] pairs
 * Example: [['role', 'viewRole'], ['role', 'addRole']]
 * @returns {boolean}
 */
export const hasAllPermissions = (permissionList) => {
  return permissionList.every(([module, action]) => hasPermission(module, action));
};

/**
 * Check if user can view a module (has viewXXX permission)
 * @param {string} module - Module name (e.g., 'role', 'user')
 * @returns {boolean}
 */
export const canViewModule = (module) => {
  const viewPermission = `view${module.charAt(0).toUpperCase()}${module.slice(1)}`;
  return hasPermission(module, viewPermission);
};

/**
 * Get the first accessible route based on user permissions
 * Routes are checked in order of priority
 * @returns {string} - First accessible route path
 */
export const getFirstAccessibleRoute = () => {
  // Define routes with their required permissions in priority order
  const routes = [
    { path: '/dashboard', module: 'dashboard', action: 'viewDashboard' },
    { path: '/devices', module: 'device', action: 'viewDevice' },
    { path: '/license-management/licenses', module: 'license', action: 'viewLicense' },
    { path: '/catalog/units', module: 'unit', action: 'viewUnit' },
    { path: '/users/accounts', module: 'user', action: 'viewUser' },
    { path: '/users/roles', module: 'role', action: 'viewRole' },
    { path: '/reports/access', module: 'report', action: 'viewAccessReport' },
    { path: '/reports/devices', module: 'report', action: 'viewDeviceReport' },
    { path: '/system/settings', module: 'setting', action: 'viewSetting' },
    { path: '/system/logs', module: 'systemlog', action: 'viewSystemLog' },
  ];

  for (const route of routes) {
    if (hasPermission(route.module, route.action)) {
      return route.path;
    }
  }

  // Fallback to dashboard (will show access denied if no permissions)
  return '/dashboard';
};

export default {
  getPermissions,
  transformPermissions,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  canViewModule,
  getFirstAccessibleRoute,
};
